import React, { useState, useEffect, useRef } from 'react';
import { AppData, StoreType } from '../types';
import { StoreBadge } from './StoreBadge';
import { AppIcon } from './AppIcon';
import { getGrowthClassification } from './GrowthScoreGauge';
import { appCatalog, STOREFRONT_COUNTRIES } from '../services/appCatalogService';
import { Search, ChevronRight, Star, Loader2, Globe } from 'lucide-react';

interface GlobalSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectApp: (appId: string) => void;
  onSelectDeveloper?: (devSlug: string) => void;
  onSelectKeyword?: (kw: string) => void;
}

export const GlobalSearchModal: React.FC<GlobalSearchModalProps> = ({
  isOpen,
  onClose,
  onSelectApp,
  onSelectDeveloper,
  onSelectKeyword
}) => {
  const [query, setQuery] = useState('');
  const [selectedStore, setSelectedStore] = useState<StoreType>('all');
  const [selectedCountry, setSelectedCountry] = useState<string>(appCatalog.getSelectedCountry());
  const [isLoading, setIsLoading] = useState(false);
  const [searchResults, setSearchResults] = useState<AppData[]>([]);
  const [devResults, setDevResults] = useState<any[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<any>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 50);
      // Preload trending
      loadInitialApps();
    } else {
      setQuery('');
      setSearchResults([]);
      setDevResults([]);
    }
  }, [isOpen]);

  const loadInitialApps = async () => {
    setIsLoading(true);
    try {
      const res = await appCatalog.searchApps({
        q: '',
        country: selectedCountry,
        store: selectedStore,
        limit: 25
      });
      setSearchResults(res.results || []);
    } catch (e) {
      console.warn('Error loading search modal apps:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) return;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      try {
        const [appRes, devRes] = await Promise.all([
          appCatalog.searchApps({
            q: query,
            country: selectedCountry,
            store: selectedStore,
            limit: 30
          }),
          query.trim() ? appCatalog.getDevelopers(query, 1, 5) : Promise.resolve({ results: [] })
        ]);

        setSearchResults(appRes.results || []);
        setDevResults(devRes.results || []);
      } catch (err) {
        console.warn('Search execution error:', err);
      } finally {
        setIsLoading(false);
      }
    }, 280);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, selectedStore, selectedCountry, isOpen]);

  if (!isOpen) return null;

  const handleAppClick = (appId: string) => {
    onSelectApp(appId);
    onClose();
  };

  const handleDevClick = (devSlug: string) => {
    if (onSelectDeveloper) onSelectDeveloper(devSlug);
    onClose();
  };

  return (
    <div id="global_search_modal_overlay" className="fixed inset-0 z-50 flex items-start justify-center pt-12 md:pt-20 p-4 bg-slate-900/60 backdrop-blur-md animate-fadeIn">
      <div id="global_search_modal_container" className="bg-white rounded-3xl max-w-2xl w-full shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[85vh] animate-scaleUp">
        
        {/* Search Input Bar */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center gap-3">
          <Search className="w-5 h-5 text-slate-400 flex-shrink-0" strokeWidth={2} />
          <input
            id="global_search_input"
            ref={inputRef}
            type="text"
            placeholder="Search any app on the App Store (e.g. Zoom, WhatsApp, Netflix)..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full text-base sm:text-lg font-medium text-slate-900 placeholder:text-slate-400 bg-transparent focus:outline-none"
          />
          {isLoading && (
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin flex-shrink-0" />
          )}
          {query && !isLoading && (
            <button
              id="clear_search_btn"
              onClick={() => setQuery('')}
              className="text-xs text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded-lg"
            >
              Clear
            </button>
          )}
          <button
            id="close_search_modal_btn"
            onClick={onClose}
            className="text-xs font-semibold bg-slate-100 hover:bg-slate-200 text-slate-600 px-2.5 py-1.5 rounded-xl transition-colors"
          >
            ESC
          </button>
        </div>

        {/* Storefront Country & Store Filter Bar */}
        <div className="px-5 py-2.5 bg-slate-50 border-b border-slate-100 flex items-center justify-between flex-wrap gap-2 text-xs">
          <div className="flex items-center gap-2">
            {/* Storefront Country Selector */}
            <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-lg px-2.5 py-1 text-slate-700 font-semibold shadow-xs">
              <Globe className="w-3.5 h-3.5 text-blue-600" />
              <select
                id="search_storefront_country_select"
                value={selectedCountry}
                onChange={(e) => {
                  setSelectedCountry(e.target.value);
                  appCatalog.setSelectedCountry(e.target.value);
                }}
                className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer"
              >
                {STOREFRONT_COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.flag} {c.code} ({c.name})
                  </option>
                ))}
              </select>
            </div>

            {/* Store Type */}
            <div className="flex items-center gap-1 font-semibold text-slate-600">
              {(['all', 'appstore', 'googleplay'] as const).map((st) => (
                <button
                  key={st}
                  id={`search_store_btn_${st}`}
                  onClick={() => setSelectedStore(st)}
                  className={`px-2.5 py-1 rounded-lg transition-all ${
                    selectedStore === st
                      ? 'bg-white text-blue-600 shadow-xs border border-slate-200 font-bold'
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {st === 'all' ? 'All' : st === 'appstore' ? 'App Store' : 'Google Play'}
                </button>
              ))}
            </div>
          </div>

          <div className="hidden sm:flex items-center gap-1.5 text-slate-400">
            <span>Try:</span>
            {['Zoom', 'WhatsApp', 'Netflix', 'Uber', 'Spotify'].map((sample) => (
              <button
                key={sample}
                onClick={() => setQuery(sample)}
                className="hover:text-blue-600 hover:underline"
              >
                {sample}
              </button>
            ))}
          </div>
        </div>

        {/* Search Results List */}
        <div id="search_results_scroll_container" className="overflow-y-auto p-3 divide-y divide-slate-50 flex-1">
          {/* Developers Match */}
          {devResults.length > 0 && (
            <div className="pb-3 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-3 block mb-1">
                Developers
              </span>
              {devResults.map((d) => (
                <div
                  key={d.id}
                  id={`search_dev_${d.slug}`}
                  onClick={() => handleDevClick(d.slug)}
                  className="p-3 rounded-2xl hover:bg-slate-50 flex items-center justify-between cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AppIcon
                      src={d.avatarUrl}
                      name={d.name}
                      size="xs"
                      shape="squircle"
                      className="w-8 h-8 rounded-xl flex-shrink-0"
                    />
                    <div>
                      <span className="font-bold text-slate-900 text-sm">{d.name}</span>
                      <span className="text-xs text-slate-400 ml-2">{d.hqCountry || 'Global'} • {d.appsCount || 1} apps</span>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-0.5 text-xs text-blue-600 font-semibold">
                    <span>View Developer</span>
                    <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Apps List */}
          <div>
            <div className="flex items-center justify-between px-3 mb-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                {query.trim() ? `Search Results (${searchResults.length})` : `Discovered Apps in ${selectedCountry}`}
              </span>
              <span className="text-[10px] text-emerald-600 font-medium bg-emerald-50 px-2 py-0.5 rounded-md">
                Live App Store Feed
              </span>
            </div>

            {searchResults.length === 0 && !isLoading ? (
              <div className="py-12 text-center text-slate-400 text-sm">
                <p className="font-semibold text-slate-700 mb-1">No apps found</p>
                <p className="text-xs">Try searching for app names like &ldquo;Slack&rdquo;, &ldquo;Notion&rdquo;, or &ldquo;Tinder&rdquo;</p>
              </div>
            ) : (
              searchResults.map((app) => {
                const { label, bg } = getGrowthClassification(app.growthScore);
                return (
                  <div
                    key={app.id}
                    id={`search_item_${app.id}`}
                    onClick={() => handleAppClick(app.id)}
                    className="p-3 rounded-2xl hover:bg-slate-50 flex items-center justify-between cursor-pointer transition-colors group"
                  >
                    <div className="flex items-center gap-3.5 min-w-0">
                      <AppIcon
                        app={app}
                        size="md"
                        className="w-11 h-11 rounded-2xl shadow-xs group-hover:scale-105 transition-transform flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-slate-900 text-sm group-hover:text-blue-600 transition-colors truncate">
                            {app.name}
                          </span>
                          <StoreBadge store={app.primaryStore} />
                        </div>
                        <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                          <span className="truncate max-w-[140px]">{app.developer}</span>
                          <span>•</span>
                          <span>{app.category}</span>
                          <span>•</span>
                          <span className="inline-flex items-center gap-1 text-slate-700 font-semibold">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" strokeWidth={1.5} />
                            <span>{app.rating.toFixed(1)}</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="text-right flex-shrink-0 ml-3">
                      <div className="text-sm font-bold text-slate-900">
                        #{app.currentRank}
                      </div>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${bg}`}>
                        {app.growthScore} • {label}
                      </span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Footer shortcuts */}
        <div className="px-5 py-3 bg-slate-50 border-t border-slate-100 flex items-center justify-between text-[11px] text-slate-400">
          <span>Global App Store Database • Real Apple Metadata</span>
          <span>Indexed dynamically on demand</span>
        </div>
      </div>
    </div>
  );
};
